import React, { useEffect, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import './AdminPricing.css';

const AdminPricing = () => {
  const [rules, setRules] = useState([]);
  const [requests, setRequests] = useState([]);
  const [segments, setSegments] = useState([]);
  const [dynamicRules, setDynamicRules] = useState([]);
  const [priceCalculations, setPriceCalculations] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [form, setForm] = useState({ scope: 'global', category: '', product_id: '', floor_price: '', ceiling_price: '', surge_percentage: '', promo_percentage: '', is_active: true });
  const [segmentForm, setSegmentForm] = useState({ segment_name: '', description: '', criteria: '{}', pricing_multiplier: 1.0, is_active: true });
  const [dynamicForm, setDynamicForm] = useState({ 
    rule_name: '', 
    product_id: '', 
    category: '', 
    segment_id: '', 
    behavior_trigger: '', 
    price_adjustment_type: 'percentage', 
    price_adjustment_value: '', 
    min_price: '', 
    max_price: '', 
    priority: 0, 
    is_active: true 
  });

  const adminHeaders = {
    'Content-Type': 'application/json',
    'x-admin-key': localStorage.getItem('adminKey') || 'dev-admin-key'
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [r1, r2, r3, r4, r5, r6] = await Promise.all([
        fetch('/api/admin/pricing-rules', { headers: adminHeaders }),
        fetch('/api/admin/price-requests?status=pending', { headers: adminHeaders }),
        fetch('/api/admin/consumer-segments', { headers: adminHeaders }),
        fetch('/api/admin/dynamic-pricing-rules', { headers: adminHeaders }),
        fetch('/api/admin/price-calculations?limit=50', { headers: adminHeaders }),
        fetch('/api/admin/consumer-behavior-analytics?days=7', { headers: adminHeaders })
      ]);
      const [rulesData, reqData, segmentsData, dynamicData, calculationsData, analyticsData] = await Promise.all([
        r1.json(), r2.json(), r3.json(), r4.json(), r5.json(), r6.json()
      ]);
      setRules(rulesData || []);
      setRequests(reqData || []);
      setSegments(segmentsData || []);
      setDynamicRules(dynamicData || []);
      setPriceCalculations(calculationsData || []);
      setAnalytics(analyticsData || null);
    } catch (_) {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const createRule = async (e) => {
    e.preventDefault();
    const body = { ...form, is_active: form.is_active ? 1 : 0 };
    await fetch('/api/admin/pricing-rules', { method: 'POST', headers: adminHeaders, body: JSON.stringify(body) });
    setForm({ scope: 'global', category: '', product_id: '', floor_price: '', ceiling_price: '', surge_percentage: '', promo_percentage: '', is_active: true });
    fetchAll();
  };

  const createSegment = async (e) => {
    e.preventDefault();
    try {
      const criteria = JSON.parse(segmentForm.criteria);
      const body = { ...segmentForm, criteria, is_active: segmentForm.is_active ? 1 : 0 };
      await fetch('/api/admin/consumer-segments', { method: 'POST', headers: adminHeaders, body: JSON.stringify(body) });
      setSegmentForm({ segment_name: '', description: '', criteria: '{}', pricing_multiplier: 1.0, is_active: true });
      fetchAll();
    } catch (error) {
      alert('Invalid JSON in criteria field');
    }
  };

  const createDynamicRule = async (e) => {
    e.preventDefault();
    const body = { ...dynamicForm, is_active: dynamicForm.is_active ? 1 : 0 };
    await fetch('/api/admin/dynamic-pricing-rules', { method: 'POST', headers: adminHeaders, body: JSON.stringify(body) });
    setDynamicForm({ 
      rule_name: '', 
      product_id: '', 
      category: '', 
      segment_id: '', 
      behavior_trigger: '', 
      price_adjustment_type: 'percentage', 
      price_adjustment_value: '', 
      min_price: '', 
      max_price: '', 
      priority: 0, 
      is_active: true 
    });
    fetchAll();
  };

  const reviewRequest = async (id, action) => {
    await fetch(`/api/admin/price-requests/${id}/review`, { method: 'POST', headers: adminHeaders, body: JSON.stringify({ action }) });
    fetchAll();
  };

  // Calculate pricing metrics
  const pricingMetrics = {
    totalRules: rules.length,
    activeRules: rules.filter(r => r.is_active).length,
    pendingRequests: requests.length,
    totalSegments: segments.length,
    avgPriceAdjustment: dynamicRules.length > 0 ? 
      (dynamicRules.reduce((sum, rule) => sum + parseFloat(rule.price_adjustment_value || 0), 0) / dynamicRules.length).toFixed(1) : 0
  };

  return (
    <div className="admin-pricing">
      <div className="pricing-header">
        <div className="pricing-header-content">
          <div className="pricing-title-section">
            <h1 className="pricing-title">Pricing Management</h1>
            <p className="pricing-subtitle">Control pricing rules, dynamic adjustments, and analyze pricing performance</p>
          </div>
          <div className="pricing-actions">
            <button className="pricing-action-btn primary">
              <LucideIcons.Plus size={16} />
              New Rule
            </button>
            <button className="pricing-action-btn secondary">
              <LucideIcons.Download size={16} />
              Export Data
            </button>
          </div>
        </div>
        
        <div className="pricing-tabs">
          <button 
            className={`pricing-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <LucideIcons.LayoutDashboard size={18} />
            <span>Overview</span>
          </button>
          <button 
            className={`pricing-tab ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
          >
            <LucideIcons.Settings size={18} />
            <span>Basic Rules</span>
          </button>
          <button 
            className={`pricing-tab ${activeTab === 'dynamic' ? 'active' : ''}`}
            onClick={() => setActiveTab('dynamic')}
          >
            <LucideIcons.TrendingUp size={18} />
            <span>Dynamic Pricing</span>
          </button>
          <button 
            className={`pricing-tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <LucideIcons.BarChart3 size={18} />
            <span>Analytics</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="pricing-loading">
          <div className="loading-spinner"></div>
          <p>Loading pricing data...</p>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <div className="pricing-overview">
              <div className="pricing-metrics-grid">
                <div className="pricing-metric-card">
                  <div className="metric-icon blue">
                    <LucideIcons.Settings size={24} />
                  </div>
                  <div className="metric-content">
                    <h3>Total Rules</h3>
                    <p className="metric-value">{pricingMetrics.totalRules}</p>
                    <span className="metric-label">Pricing rules configured</span>
                  </div>
                </div>
                
                <div className="pricing-metric-card">
                  <div className="metric-icon green">
                    <LucideIcons.CheckCircle size={24} />
                  </div>
                  <div className="metric-content">
                    <h3>Active Rules</h3>
                    <p className="metric-value">{pricingMetrics.activeRules}</p>
                    <span className="metric-label">Currently active</span>
                  </div>
                </div>
                
                <div className="pricing-metric-card">
                  <div className="metric-icon orange">
                    <LucideIcons.Clock size={24} />
                  </div>
                  <div className="metric-content">
                    <h3>Pending Requests</h3>
                    <p className="metric-value">{pricingMetrics.pendingRequests}</p>
                    <span className="metric-label">Awaiting review</span>
                  </div>
                </div>
                
                <div className="pricing-metric-card">
                  <div className="metric-icon purple">
                    <LucideIcons.Users size={24} />
                  </div>
                  <div className="metric-content">
                    <h3>Customer Segments</h3>
                    <p className="metric-value">{pricingMetrics.totalSegments}</p>
                    <span className="metric-label">Segments defined</span>
                  </div>
                </div>
              </div>

              <div className="pricing-overview-content">
                <div className="overview-section">
                  <div className="section-header">
                    <h2>Recent Price Requests</h2>
                    <button className="btn-secondary">View All</button>
                  </div>
                  <div className="requests-preview">
                    {requests.slice(0, 3).map(req => (
                      <div key={req.id} className="request-item">
                        <div className="request-info">
                          <h4>{req.name}</h4>
                          <p>{req.vendor_name} • {req.category}</p>
                        </div>
                        <div className="request-details">
                          <span className="request-price">₹{req.requested_price}</span>
                          <div className="request-actions">
                            <button className="btn-approve" onClick={() => reviewRequest(req.id, 'approve')}>
                              <LucideIcons.Check size={16} />
                            </button>
                            <button className="btn-reject" onClick={() => reviewRequest(req.id, 'reject')}>
                              <LucideIcons.X size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {requests.length === 0 && (
                      <div className="empty-state">
                        <LucideIcons.CheckCircle size={48} />
                        <p>No pending price requests</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="overview-section">
                  <div className="section-header">
                    <h2>Active Pricing Rules</h2>
                    <button className="btn-secondary">Manage Rules</button>
                  </div>
                  <div className="rules-preview">
                    {rules.filter(r => r.is_active).slice(0, 3).map(rule => (
                      <div key={rule.id} className="rule-item">
                        <div className="rule-info">
                          <h4>{rule.scope} Rule</h4>
                          <p>{rule.category || 'Global'} • Floor: ₹{rule.floor_price || 'N/A'}</p>
                        </div>
                        <div className="rule-status">
                          <span className="status-badge active">Active</span>
                        </div>
                      </div>
                    ))}
                    {rules.filter(r => r.is_active).length === 0 && (
                      <div className="empty-state">
                        <LucideIcons.Settings size={48} />
                        <p>No active pricing rules</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'basic' && (
            <div className="pricing-basic">
              <div className="pricing-section">
                <div className="section-header">
                  <h2>Create Basic Pricing Rule</h2>
                  <p>Set up fundamental pricing rules for your products</p>
                </div>
                
                <div className="pricing-form-container">
                  <form onSubmit={createRule} className="pricing-form">
                    <div className="form-group">
                      <label className="form-label">
                        <LucideIcons.Target size={16} />
                        Scope
                      </label>
                      <select 
                        className="form-select"
                        value={form.scope} 
                        onChange={e => setForm({ ...form, scope: e.target.value })}
                      >
                        <option value="global">Global</option>
                        <option value="category">Category</option>
                        <option value="product">Product</option>
                      </select>
                    </div>

                    {form.scope === 'category' && (
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.Tag size={16} />
                          Category
                        </label>
                        <input 
                          className="form-input"
                          value={form.category} 
                          onChange={e => setForm({ ...form, category: e.target.value })}
                          placeholder="Enter category name"
                        />
                      </div>
                    )}

                    {form.scope === 'product' && (
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.Package size={16} />
                          Product ID
                        </label>
                        <input 
                          className="form-input"
                          value={form.product_id} 
                          onChange={e => setForm({ ...form, product_id: e.target.value })}
                          placeholder="Enter product ID"
                        />
                      </div>
                    )}

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.ArrowDown size={16} />
                          Floor Price
                        </label>
                        <input 
                          type="number" 
                          step="0.01" 
                          className="form-input"
                          value={form.floor_price} 
                          onChange={e => setForm({ ...form, floor_price: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.ArrowUp size={16} />
                          Ceiling Price
                        </label>
                        <input 
                          type="number" 
                          step="0.01" 
                          className="form-input"
                          value={form.ceiling_price} 
                          onChange={e => setForm({ ...form, ceiling_price: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.TrendingUp size={16} />
                          Surge Percentage
                        </label>
                        <input 
                          type="number" 
                          step="0.01" 
                          className="form-input"
                          value={form.surge_percentage} 
                          onChange={e => setForm({ ...form, surge_percentage: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.Percent size={16} />
                          Promo Percentage
                        </label>
                        <input 
                          type="number" 
                          step="0.01" 
                          className="form-input"
                          value={form.promo_percentage} 
                          onChange={e => setForm({ ...form, promo_percentage: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={form.is_active} 
                          onChange={e => setForm({ ...form, is_active: e.target.checked })}
                        />
                        <span className="checkbox-custom"></span>
                        <span>Activate this rule immediately</span>
                      </label>
                    </div>

                    <button className="btn-primary" type="submit">
                      <LucideIcons.Plus size={16} />
                      Create Pricing Rule
                    </button>
                  </form>
                </div>
              </div>

              <div className="pricing-section">
                <div className="section-header">
                  <h2>Active Basic Rules</h2>
                  <p>Manage your current pricing rules and their status</p>
                </div>
                
                <div className="rules-table-container">
                  <div className="table-header">
                    <div className="table-actions">
                      <button className="btn-secondary">
                        <LucideIcons.Filter size={16} />
                        Filter
                      </button>
                      <button className="btn-secondary">
                        <LucideIcons.Download size={16} />
                        Export
                      </button>
                    </div>
                  </div>
                  
                  <div className="rules-grid">
                    {rules.map(rule => (
                      <div key={rule.id} className="rule-card">
                        <div className="rule-header">
                          <div className="rule-title">
                            <h4>{rule.scope.charAt(0).toUpperCase() + rule.scope.slice(1)} Rule</h4>
                            <span className="rule-scope">
                              {rule.category ? rule.category : rule.product_id ? `Product #${rule.product_id}` : 'Global'}
                            </span>
                          </div>
                          <div className="rule-status">
                            <span className={`status-badge ${rule.is_active ? 'active' : 'inactive'}`}>
                              {rule.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="rule-details">
                          <div className="detail-item">
                            <span className="detail-label">Floor Price</span>
                            <span className="detail-value">₹{rule.floor_price || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Ceiling Price</span>
                            <span className="detail-value">₹{rule.ceiling_price || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Surge %</span>
                            <span className="detail-value">{rule.surge_percentage || 'N/A'}%</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Promo %</span>
                            <span className="detail-value">{rule.promo_percentage || 'N/A'}%</span>
                          </div>
                        </div>
                        
                        <div className="rule-actions">
                          <button className="action-btn edit">
                            <LucideIcons.Edit size={16} />
                          </button>
                          <button className="action-btn delete">
                            <LucideIcons.Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {rules.length === 0 && (
                      <div className="empty-state">
                        <LucideIcons.Settings size={48} />
                        <h3>No pricing rules configured</h3>
                        <p>Create your first pricing rule to get started</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="pricing-section">
                <div className="section-header">
                  <h2>Vendor Price Requests</h2>
                  <p>Review and approve vendor price change requests</p>
                </div>
                
                <div className="requests-table-container">
                  <div className="table-header">
                    <div className="table-actions">
                      <button className="btn-secondary">
                        <LucideIcons.Filter size={16} />
                        Filter
                      </button>
                      <button className="btn-secondary">
                        <LucideIcons.Download size={16} />
                        Export
                      </button>
                    </div>
                  </div>
                  
                  <div className="requests-grid">
                    {requests.map(req => (
                      <div key={req.id} className="request-card">
                        <div className="request-header">
                          <div className="request-title">
                            <h4>{req.name}</h4>
                            <span className="request-sku">SKU: {req.sku}</span>
                          </div>
                          <div className="request-price">
                            <span className="price-value">₹{req.requested_price}</span>
                          </div>
                        </div>
                        
                        <div className="request-details">
                          <div className="detail-row">
                            <span className="detail-label">Vendor</span>
                            <span className="detail-value">{req.vendor_name}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Category</span>
                            <span className="detail-value">{req.category}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Reason</span>
                            <span className="detail-value">{req.reason || 'No reason provided'}</span>
                          </div>
                        </div>
                        
                        <div className="request-actions">
                          <button className="btn-approve" onClick={() => reviewRequest(req.id, 'approve')}>
                            <LucideIcons.Check size={16} />
                            Approve
                          </button>
                          <button className="btn-reject" onClick={() => reviewRequest(req.id, 'reject')}>
                            <LucideIcons.X size={16} />
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {requests.length === 0 && (
                      <div className="empty-state">
                        <LucideIcons.CheckCircle size={48} />
                        <h3>No pending requests</h3>
                        <p>All price requests have been reviewed</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'dynamic' && (
            <div className="pricing-dynamic">
              <div className="pricing-section">
                <div className="section-header">
                  <h2>Dynamic Pricing Configuration</h2>
                  <p>Set up intelligent pricing rules based on customer behavior and market conditions</p>
                </div>
                
                <div className="dynamic-forms-grid">
                  <div className="form-section">
                    <div className="form-section-header">
                      <h3>Create Consumer Segment</h3>
                      <p>Define customer groups for targeted pricing</p>
                    </div>
                    
                    <form onSubmit={createSegment} className="pricing-form">
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.Users size={16} />
                          Segment Name
                        </label>
                        <input 
                          className="form-input"
                          value={segmentForm.segment_name} 
                          onChange={e => setSegmentForm({ ...segmentForm, segment_name: e.target.value })} 
                          required 
                          placeholder="e.g., Premium Customers"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.FileText size={16} />
                          Description
                        </label>
                        <textarea 
                          className="form-input"
                          value={segmentForm.description} 
                          onChange={e => setSegmentForm({ ...segmentForm, description: e.target.value })} 
                          rows="3"
                          placeholder="Describe this customer segment..."
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.Code size={16} />
                          Criteria (JSON)
                        </label>
                        <textarea 
                          className="form-input"
                          value={segmentForm.criteria} 
                          onChange={e => setSegmentForm({ ...segmentForm, criteria: e.target.value })} 
                          rows="3" 
                          placeholder='{"order_count": {"min": 3}, "total_spent": {"min": 500}}'
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.Percent size={16} />
                          Pricing Multiplier
                        </label>
                        <input 
                          type="number" 
                          step="0.01" 
                          className="form-input"
                          value={segmentForm.pricing_multiplier} 
                          onChange={e => setSegmentForm({ ...segmentForm, pricing_multiplier: parseFloat(e.target.value) })}
                          placeholder="1.0"
                        />
                      </div>
                      
                      <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={segmentForm.is_active} 
                            onChange={e => setSegmentForm({ ...segmentForm, is_active: e.target.checked })}
                          />
                          <span className="checkbox-custom"></span>
                          <span>Activate this segment</span>
                        </label>
                      </div>
                      
                      <button className="btn-primary" type="submit">
                        <LucideIcons.Plus size={16} />
                        Create Segment
                      </button>
                    </form>
                  </div>

                  <div className="form-section">
                    <div className="form-section-header">
                      <h3>Create Dynamic Pricing Rule</h3>
                      <p>Set up rules that adjust prices based on triggers</p>
                    </div>
                    
                    <form onSubmit={createDynamicRule} className="pricing-form">
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.Settings size={16} />
                          Rule Name
                        </label>
                        <input 
                          className="form-input"
                          value={dynamicForm.rule_name} 
                          onChange={e => setDynamicForm({ ...dynamicForm, rule_name: e.target.value })} 
                          required 
                          placeholder="e.g., High Demand Surge"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.Zap size={16} />
                          Behavior Trigger
                        </label>
                        <select 
                          className="form-select"
                          value={dynamicForm.behavior_trigger} 
                          onChange={e => setDynamicForm({ ...dynamicForm, behavior_trigger: e.target.value })}
                        >
                          <option value="">Select Trigger</option>
                          <option value="new_customer">New Customer</option>
                          <option value="returning_customer">Returning Customer</option>
                          <option value="high_demand">High Demand</option>
                          <option value="low_demand">Low Demand</option>
                          <option value="price_sensitive">Price Sensitive</option>
                          <option value="premium_customer">Premium Customer</option>
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.TrendingUp size={16} />
                          Adjustment Type
                        </label>
                        <select 
                          className="form-select"
                          value={dynamicForm.price_adjustment_type} 
                          onChange={e => setDynamicForm({ ...dynamicForm, price_adjustment_type: e.target.value })}
                        >
                          <option value="percentage">Percentage</option>
                          <option value="fixed">Fixed Amount</option>
                          <option value="multiplier">Multiplier</option>
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.IndianRupee size={16} />
                          Adjustment Value
                        </label>
                        <input 
                          type="number" 
                          step="0.01" 
                          className="form-input"
                          value={dynamicForm.price_adjustment_value} 
                          onChange={e => setDynamicForm({ ...dynamicForm, price_adjustment_value: e.target.value })} 
                          required 
                          placeholder="0.00"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          <LucideIcons.Star size={16} />
                          Priority
                        </label>
                        <input 
                          type="number" 
                          className="form-input"
                          value={dynamicForm.priority} 
                          onChange={e => setDynamicForm({ ...dynamicForm, priority: parseInt(e.target.value) })}
                          placeholder="0"
                        />
                      </div>
                      
                      <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={dynamicForm.is_active} 
                            onChange={e => setDynamicForm({ ...dynamicForm, is_active: e.target.checked })}
                          />
                          <span className="checkbox-custom"></span>
                          <span>Activate this rule</span>
                        </label>
                      </div>
                      
                      <button className="btn-primary" type="submit">
                        <LucideIcons.Plus size={16} />
                        Create Dynamic Rule
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              <div className="pricing-section">
                <div className="section-header">
                  <h2>Consumer Segments</h2>
                  <p>Manage customer segments for targeted pricing strategies</p>
                </div>
                
                <div className="segments-grid">
                  {segments.map(segment => (
                    <div key={segment.id} className="segment-card">
                      <div className="segment-header">
                        <h4>{segment.segment_name}</h4>
                        <span className={`status-badge ${segment.is_active ? 'active' : 'inactive'}`}>
                          {segment.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="segment-description">{segment.description}</p>
                      <div className="segment-details">
                        <div className="detail-item">
                          <span className="detail-label">Multiplier</span>
                          <span className="detail-value">{segment.pricing_multiplier}x</span>
                        </div>
                      </div>
                      <div className="segment-actions">
                        <button className="action-btn edit">
                          <LucideIcons.Edit size={16} />
                        </button>
                        <button className="action-btn delete">
                          <LucideIcons.Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {segments.length === 0 && (
                    <div className="empty-state">
                      <LucideIcons.Users size={48} />
                      <h3>No segments created</h3>
                      <p>Create customer segments to enable targeted pricing</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="pricing-section">
                <div className="section-header">
                  <h2>Dynamic Pricing Rules</h2>
                  <p>View and manage your dynamic pricing rules</p>
                </div>
                
                <div className="dynamic-rules-grid">
                  {dynamicRules.map(rule => (
                    <div key={rule.id} className="dynamic-rule-card">
                      <div className="rule-header">
                        <div className="rule-title">
                          <h4>{rule.rule_name}</h4>
                          <span className="rule-priority">Priority: {rule.priority}</span>
                        </div>
                        <div className="rule-status">
                          <span className={`status-badge ${rule.is_active ? 'active' : 'inactive'}`}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="rule-details">
                        <div className="detail-item">
                          <span className="detail-label">Trigger</span>
                          <span className="detail-value">{rule.behavior_trigger || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Adjustment</span>
                          <span className="detail-value">{rule.price_adjustment_type} - {rule.price_adjustment_value}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Product</span>
                          <span className="detail-value">{rule.product_name || 'All Products'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Segment</span>
                          <span className="detail-value">{rule.segment_name || 'All Segments'}</span>
                        </div>
                      </div>
                      
                      <div className="rule-actions">
                        <button className="action-btn edit">
                          <LucideIcons.Edit size={16} />
                        </button>
                        <button className="action-btn delete">
                          <LucideIcons.Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {dynamicRules.length === 0 && (
                    <div className="empty-state">
                      <LucideIcons.TrendingUp size={48} />
                      <h3>No dynamic rules configured</h3>
                      <p>Create dynamic pricing rules to automate price adjustments</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="pricing-analytics">
              <div className="pricing-section">
                <div className="section-header">
                  <h2>Price Calculation History</h2>
                  <p>Track how pricing rules are being applied to products</p>
                </div>
                
                <div className="calculations-table-container">
                  <div className="table-header">
                    <div className="table-actions">
                      <button className="btn-secondary">
                        <LucideIcons.Filter size={16} />
                        Filter
                      </button>
                      <button className="btn-secondary">
                        <LucideIcons.Download size={16} />
                        Export
                      </button>
                    </div>
                  </div>
                  
                  <div className="calculations-grid">
                    {priceCalculations.slice(0, 20).map(calc => (
                      <div key={calc.id} className="calculation-card">
                        <div className="calculation-header">
                          <div className="calculation-title">
                            <h4>{calc.product_name}</h4>
                            <span className="calculation-time">{new Date(calc.created_at).toLocaleString()}</span>
                          </div>
                          <div className="price-comparison">
                            <span className="base-price">₹{calc.base_price}</span>
                            <LucideIcons.ArrowRight size={16} />
                            <span className="final-price">₹{calc.final_price}</span>
                          </div>
                        </div>
                        
                        <div className="calculation-details">
                          <div className="detail-row">
                            <span className="detail-label">Customer</span>
                            <span className="detail-value">{calc.customer_name || 'Anonymous'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Applied Rules</span>
                            <span className="detail-value">
                              {(() => {
                                if (!calc.applied_rules) return 'None';
                                try {
                                  const rules = JSON.parse(calc.applied_rules);
                                  return Array.isArray(rules) ? rules.join(', ') : calc.applied_rules;
                                } catch (e) {
                                  // If JSON parsing fails, treat as comma-separated string
                                  return calc.applied_rules;
                                }
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {priceCalculations.length === 0 && (
                      <div className="empty-state">
                        <LucideIcons.BarChart3 size={48} />
                        <h3>No calculations found</h3>
                        <p>Price calculations will appear here as customers browse products</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {analytics && (
                <div className="pricing-section">
                  <div className="section-header">
                    <h2>Consumer Behavior Analytics</h2>
                    <p>Insights from the last 7 days of customer interactions</p>
                  </div>
                  
                  <div className="analytics-grid">
                    <div className="analytics-section">
                      <div className="analytics-header">
                        <h3>Behavior Summary</h3>
                        <LucideIcons.TrendingUp size={20} />
                      </div>
                      
                      <div className="analytics-list">
                        {analytics.analytics && analytics.analytics.slice(0, 10).map((item, index) => (
                          <div key={index} className="analytics-item">
                            <div className="analytics-item-header">
                              <h4>{item.product_name}</h4>
                              <span className="behavior-count">{item.count} times</span>
                            </div>
                            <div className="analytics-item-details">
                              <span className="behavior-type">{item.behavior_type}</span>
                              <span className="avg-price">
                                Avg Price: ₹{item.avg_price_shown?.toFixed(2) || 'N/A'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="analytics-section">
                      <div className="analytics-header">
                        <h3>Demand Patterns</h3>
                        <LucideIcons.Clock size={20} />
                      </div>
                      
                      <div className="demand-list">
                        {analytics.demandPatterns && analytics.demandPatterns.slice(0, 10).map((pattern, index) => (
                          <div key={index} className="demand-item">
                            <div className="demand-item-header">
                              <h4>{pattern.product_name}</h4>
                              <span className="demand-hour">Hour {pattern.hour}</span>
                            </div>
                            <div className="demand-item-details">
                              <span className="views-count">{pattern.views} views</span>
                              <span className="avg-price">
                                Avg Price: ₹{pattern.avg_price?.toFixed(2) || 'N/A'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminPricing;


